import { getKuecheStandort, onKuecheStandortChange, setKuecheStandort } from './_konfig.js';

/**
 * Küchenstandort-Umschalter auf der Wohnungs-Detailseite.
 *
 * Blendet die Grundriss-Variante (`gr_<coord>_ks-<n>`) zur gewählten Variante
 * ein – sowohl in der Vorschau als auch in der Vergrösserung (Lightbox). Der
 * Umschalter wird serverseitig nur gerendert, wenn mehr als eine Variante als
 * Datei existiert (siehe lm_wohnung_grundriss_varianten()).
 *
 * Die Vergrösserung ist eine eigene Lightbox (kein GLightbox), damit
 *   - der Küchenstandort auch im geöffneten Zustand umgeschaltet werden kann,
 *   - auf Mobile per Pinch ins Bild gezoomt und verschoben werden kann und
 *   - der Zoom beim Umschalten der Variante erhalten bleibt.
 * Verhalten wie im Entwurf: Projekt-Export/Wohnung A-1-1.dc.html.
 *
 * Seiten-Zustand (ks1/ks2) ist mit der Konfigurator-Leiste gekoppelt:
 * Tab, Lightbox-Tab und Leisten-Block setzen denselben Standort.
 */

function ksCodeFromIndex(index) {
    return 'ks' + String(index);
}

function ksIndexFromCode(code) {
    return String(code).replace(/^ks/, '');
}

function ensureSrc(img) {
    if (!img) {
        return Promise.resolve();
    }
    var url = img.getAttribute('src') || img.getAttribute('data-src');
    if (!url) {
        return Promise.resolve();
    }
    if (img.getAttribute('src') === url && img.complete && img.naturalWidth) {
        return Promise.resolve();
    }
    return new Promise(function (resolve) {
        var settled = false;
        var done = function () {
            if (settled) {
                return;
            }
            settled = true;
            resolve();
        };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        img.src = url;
        img.removeAttribute('data-src');
        if (img.complete) {
            done();
        }
    });
}

function prefetchUrl(url) {
    if (!url) {
        return;
    }
    var probe = new Image();
    probe.src = url;
}

function initKsSwitch(root) {
    var items = root.querySelectorAll('.js-ks-item');
    var tabs = root.querySelectorAll('.js-ks-tab');
    var lightbox = root.querySelector('.js-ks-lightbox');
    var openBtn = root.querySelector('.js-ks-open');
    var actGen = 0;

    function previewImgsFor(index) {
        return root.querySelectorAll('.wohnung--ks__item.js-ks-item[data-ks="' + index + '"] img');
    }

    function fullImgsFor(index) {
        return root.querySelectorAll('.wohnung--ks__full.js-ks-item[data-ks="' + index + '"]');
    }

    function hydratePreview(index) {
        var jobs = [];
        previewImgsFor(index).forEach(function (img) {
            jobs.push(ensureSrc(img));
        });
        return Promise.all(jobs);
    }

    function prefetchIndex(index) {
        previewImgsFor(index).forEach(function (img) {
            ensureSrc(img);
        });
        fullImgsFor(index).forEach(function (img) {
            prefetchUrl(img.getAttribute('src') || img.getAttribute('data-src'));
        });
    }

    function scheduleIdlePrefetch() {
        var run = function () {
            root.querySelectorAll('.wohnung--ks__item img[data-src]').forEach(function (img) {
                ensureSrc(img);
            });
            root.querySelectorAll('.wohnung--ks__full[data-src]').forEach(function (img) {
                prefetchUrl(img.getAttribute('data-src'));
            });
        };
        var arm = function () {
            if (typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(run, { timeout: 1500 });
            } else {
                window.setTimeout(run, 400);
            }
        };
        if (document.readyState !== 'complete') {
            window.addEventListener('load', arm, { once: true });
            return;
        }
        arm();
    }

    function activate(index) {
        index = String(index);
        var gen = ++actGen;
        root.setAttribute('data-ks-active', index);

        tabs.forEach(function (tab) {
            var active = tab.getAttribute('data-ks') === index;
            tab.classList.toggle('is-active', active);
            tab.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        var incoming = [hydratePreview(index)];
        if (lightbox && !lightbox.hasAttribute('hidden')) {
            fullImgsFor(index).forEach(function (img) {
                incoming.push(ensureSrc(img));
            });
        }

        Promise.all(incoming).then(function () {
            if (gen !== actGen) {
                return;
            }
            items.forEach(function (item) {
                item.classList.toggle('is-active', item.getAttribute('data-ks') === index);
            });
        });

        setKuecheStandort(ksCodeFromIndex(index));
    }

    onKuecheStandortChange(function (code) {
        var index = ksIndexFromCode(code);
        if (String(root.getAttribute('data-ks-active')) === String(index)) {
            return;
        }
        activate(index);
    });

    tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
            activate(tab.getAttribute('data-ks'));
        });
        tab.addEventListener('pointerenter', function () {
            prefetchIndex(tab.getAttribute('data-ks'));
        });
    });

    scheduleIdlePrefetch();

    if (!lightbox || !openBtn) {
        return;
    }

    /* ----------------------------------------------------------- Lightbox */

    var viewport = lightbox.querySelector('.js-ks-viewport');
    var zoomer = lightbox.querySelector('.js-ks-zoomer');
    var closeBtn = lightbox.querySelector('.js-ks-close');
    var lastFocus = null;

    // Zoom-Zustand (scale, translateX, translateY) und aktive Geste.
    var z = { s: 1, x: 0, y: 0 };
    var gesture = null;
    var lastTap = 0;

    function applyZoom() {
        zoomer.style.transform =
            'translate(' + z.x + 'px,' + z.y + 'px) scale(' + z.s + ')';
    }

    function zoomReset() {
        z = { s: 1, x: 0, y: 0 };
        applyZoom();
    }

    function isOpen() {
        return !lightbox.hasAttribute('hidden');
    }

    function openLightbox() {
        if (isOpen()) {
            return;
        }
        lastFocus = document.activeElement;
        zoomReset();
        var activeIndex = root.getAttribute('data-ks-active') || '1';
        var activeFull = lightbox.querySelector('.wohnung--ks__full[data-ks="' + activeIndex + '"]');
        ensureSrc(activeFull);
        fullImgsFor(activeIndex).forEach(function (img) {
            if (img !== activeFull) {
                prefetchUrl(img.getAttribute('src') || img.getAttribute('data-src'));
            }
        });
        root.querySelectorAll('.wohnung--ks__full[data-src]').forEach(function (img) {
            prefetchUrl(img.getAttribute('data-src'));
        });
        lightbox.removeAttribute('hidden');
        // Im nächsten Frame öffnen, damit die Transition greift.
        requestAnimationFrame(function () {
            lightbox.classList.add('is-open');
        });
        document.body.classList.add('ks-lightbox-open');
        if (closeBtn) {
            closeBtn.focus();
        }
    }

    function closeLightbox() {
        if (!isOpen()) {
            return;
        }
        lightbox.classList.remove('is-open');
        document.body.classList.remove('ks-lightbox-open');
        gesture = null;
        var finish = function () {
            lightbox.setAttribute('hidden', '');
            zoomReset();
            lightbox.removeEventListener('transitionend', finish);
        };
        lightbox.addEventListener('transitionend', finish);
        // Fallback, falls keine Transition feuert.
        setTimeout(finish, 350);
        if (lastFocus && typeof lastFocus.focus === 'function') {
            lastFocus.focus();
        }
    }

    openBtn.addEventListener('click', openLightbox);

    if (closeBtn) {
        closeBtn.addEventListener('click', function (event) {
            event.stopPropagation();
            closeLightbox();
        });
    }

    // Klick auf den Hintergrund (nicht auf Bild oder Umschalter) schliesst.
    lightbox.addEventListener('click', function (event) {
        if (event.target.closest('.js-ks-zoomer') || event.target.closest('.wohnung--ks__lightbar') || event.target.closest('.js-ks-close')) {
            return;
        }
        closeLightbox();
    });

    document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && isOpen()) {
            closeLightbox();
        }
    });

    /* --------------------------------------------------------- Pinch/Pan */

    function dist(t) {
        return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    }

    function mid(t) {
        return {
            x: (t[0].clientX + t[1].clientX) / 2,
            y: (t[0].clientY + t[1].clientY) / 2,
        };
    }

    viewport.addEventListener('touchstart', function (event) {
        var t = event.touches;
        if (t.length === 2) {
            var box = zoomer.getBoundingClientRect();
            var center = {
                x: box.left + box.width / 2 - z.x,
                y: box.top + box.height / 2 - z.y,
            };
            var m = mid(t);
            gesture = {
                d: dist(t),
                m: { x: m.x - center.x, y: m.y - center.y },
                s: z.s,
                x: z.x,
                y: z.y,
            };
        } else if (t.length === 1) {
            var now = Date.now();
            if (now - lastTap < 300) {
                zoomReset();
                lastTap = 0;
                gesture = null;
                return;
            }
            lastTap = now;
            gesture = { m: { x: t[0].clientX, y: t[0].clientY }, x: z.x, y: z.y, pan: true };
        }
    }, { passive: true });

    viewport.addEventListener('touchmove', function (event) {
        var t = event.touches;
        var g = gesture;
        if (!g) {
            return;
        }
        if (t.length === 2 && g.d) {
            event.preventDefault();
            var k = dist(t) / g.d;
            var s = Math.min(6, Math.max(1, g.s * k));
            var r = s / g.s;
            z = {
                s: s,
                x: g.m.x - (g.m.x - g.x) * r,
                y: g.m.y - (g.m.y - g.y) * r,
            };
        } else if (t.length === 1 && g.pan && z.s > 1) {
            event.preventDefault();
            z.x = g.x + t[0].clientX - g.m.x;
            z.y = g.y + t[0].clientY - g.m.y;
        } else {
            return;
        }
        applyZoom();
    }, { passive: false });

    viewport.addEventListener('touchend', function (event) {
        if (event.touches.length === 0) {
            gesture = null;
        } else if (event.touches.length === 1) {
            gesture = {
                m: { x: event.touches[0].clientX, y: event.touches[0].clientY },
                x: z.x,
                y: z.y,
                pan: true,
            };
        }
        if (z.s <= 1.02) {
            zoomReset();
        }
    });

    // Doppelklick (Desktop) setzt den Zoom zurück.
    viewport.addEventListener('dblclick', zoomReset);
}

/**
 * Hält das ks-Argument der PDF-URL auf dem gewählten Küchenstandort, damit die
 * Broschüre denselben Grundriss und dieselbe Visualisierung enthält.
 */
function initKsPdfLinks() {
    var links = document.querySelectorAll('.js-ks-pdf[data-pdf-url]');
    if (!links.length) {
        return;
    }

    function apply(index) {
        links.forEach(function (link) {
            var base = link.getAttribute('data-pdf-url');
            link.href = base + (base.indexOf('?') === -1 ? '?' : '&') + 'ks=' + index;
        });
    }

    apply(ksIndexFromCode(getKuecheStandort()));
    onKuecheStandortChange(function (code) {
        apply(ksIndexFromCode(code));
    });
}

document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.js-ks').forEach(function (root) {
        initKsSwitch(root);
    });
    initKsPdfLinks();
});
